import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { UploadSimple, X, FilePlus, VideoCamera, Play, Image as ImageIcon, CircleNotch } from "@phosphor-icons/react";
import { uploadFile } from "@/lib/api";

const ACCEPT = "image/*,video/*";
const MAX_MB = 75;

export const AttachmentUploader = ({ attachments, setAttachments, onUploadingChange, testId = "uploader" }) => {
  const inputRef = useRef(null);
  const [uploads, setUploads] = useState([]); // {tempId, name, progress}
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (onUploadingChange) onUploadingChange(uploads.length > 0);
  }, [uploads.length, onUploadingChange]);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    for (const file of list) {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        toast.error(`${file.name}: only images and videos are allowed.`);
        continue;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${file.name} is too large (max ${MAX_MB}MB).`);
        continue;
      }
      const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
      setUploads((u) => [...u, { tempId, name: file.name, progress: 0 }]);
      try {
        const data = await uploadFile(file, (p) =>
          setUploads((u) => u.map((it) => (it.tempId === tempId ? { ...it, progress: p } : it)))
        );
        setAttachments((a) => [...a, data]);
      } catch {
        toast.error(`Failed to upload ${file.name}.`);
      } finally {
        setUploads((u) => u.filter((it) => it.tempId !== tempId));
      }
    }
  };

  const removeAttachment = (url) => setAttachments((a) => a.filter((it) => it.url !== url));

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`w-full flex flex-wrap items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 font-semibold transition-colors ${
          dragging
            ? "border-navy-600 bg-slate-50 ring-2 ring-navy-600/20 text-navy-900"
            : "border-slate-300 bg-white text-navy-900 hover:border-navy-600 hover:bg-slate-50"
        }`}
      >
        <UploadSimple size={20} weight="bold" />
        {dragging ? "Drop files to attach" : "Add photos or video"}
        <span className="text-xs font-normal text-slate-400">(drag & drop · max {MAX_MB}MB each)</span>
      </button>

      {(attachments.length > 0 || uploads.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
          {attachments.map((a) => (
            <div
              key={a.url}
              data-testid="attachment-thumb"
              className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100"
            >
              {a.content_type?.startsWith("image/") ? (
                <img src={a.url} alt={a.filename} className="w-full h-full object-cover" />
              ) : (
                <a href={a.url} target="_blank" rel="noreferrer" className="block relative w-full h-full" data-testid="attachment-video">
                  {a.poster_url ? (
                    <img src={a.poster_url} alt={a.filename} className="w-full h-full object-cover" />
                  ) : (
                    <video src={a.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  )}
                  <span className="absolute inset-0 grid place-items-center pointer-events-none">
                    <span className="w-9 h-9 grid place-items-center rounded-full bg-navy-900/70 text-white">
                      <Play size={16} weight="fill" />
                    </span>
                  </span>
                </a>
              )}
              <button
                type="button"
                aria-label="Remove attachment"
                data-testid="attachment-remove"
                onClick={() => removeAttachment(a.url)}
                className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full bg-navy-900/80 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
              >
                <X size={15} weight="bold" />
              </button>
              <span className="absolute bottom-0 inset-x-0 bg-navy-900/70 text-white text-[10px] px-1.5 py-1 truncate">
                {a.filename}
              </span>
            </div>
          ))}
          {uploads.map((u) => (
            <div
              key={u.tempId}
              className="relative aspect-square rounded-xl border border-slate-200 bg-slate-50 grid place-items-center text-navy-600"
            >
              <CircleNotch size={26} className="animate-spin" />
              <span className="absolute bottom-1 inset-x-1 text-center text-[10px] text-slate-500">{u.progress}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
